import {Dialog, Stack} from "@mui/material";
import {LevelOneMessageDetails} from "./LevelOneMessageDetails";
import PropTypes from "prop-types";
import {LinkTextButton} from "./LinkTextButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";


/**
 * A dialog component that shows a message and a link button based on the
 * level of access the user has.
 *
 * The dialog is rendered with a title and content. The title is rendered
 * as a Typography component with variant="h3" and is only shown if the
 * level of access is not 4. The content depends on the level of access and
 * is a stack of a message and a link button. If the level of access is 1,
 * the message details are also rendered. If the level of access is 4, the
 * title is rendered as a Typography component with variant="h6".
 *
 * @param {Object} props Component props
 * @param {boolean} props.show Whether or not the dialog should be shown
 * @param {Object} props.config A configuration object with the following properties:
 * - {number} level The level of access the user has
 * - {string} title The title of the dialog
 * - {string} message The message to be shown in the dialog
 * - {string} [messageDetails] The message details to be shown in the dialog
 * - {string} linkText The text of the link button
 * - {string} linkUrl The link of the link button
 * @returns {React.ReactElement} The dialog component
 */
const RestrictedAccessDialog = ({show, config}) => {

    const {level, title, message, messageDetails = '', linkText, linkUrl} = config;

    /**
     * Returns the title of the dialog.
     * If the level is 4, the title is not rendered, so an empty string is returned.
     * Otherwise, the title is returned.
     * @returns {string} The title of the dialog.
     */
    const getTitle = () => {
        if (level === 4) {
            return '';
        }
        return <Typography variant={"h3"} sx={{marginBottom: '16px'}}>{title}</Typography>;
    };

    /**
     * Returns the content of the dialog.
     * The content depends on the level of access and is a stack of a message and a link button.
     * If the level is 1, the message details are also rendered.
     * If the level is 4, the title is rendered as a Typography component with variant="h6".
     * @returns {React.ReactElement} The content of the dialog.
     */
    const getContent = () => {
        if (level === 1) {
            return <Stack direction={"column"}>
                <Typography variant={"h6"} sx={{marginBottom: '8px'}}>{message}</Typography>
                <LevelOneMessageDetails
                    message={messageDetails}
                    linkText={linkText}
                    linkUrl={linkUrl}/>
            </Stack>;
        } else if (level === 3) {
            return <Stack direction={"column"}>
                <Typography variant={"h6"} sx={{marginBottm: '8px'}}>{message}</Typography>
                <LinkTextButton text={linkText} url={linkUrl}/>
            </Stack>;
        } else if (level === 4) {
            return <Stack direction={"column"}>
                <Typography variant={"h6"} sx={{marginBottom: '8px'}}>
                    {title}
                </Typography>
                {message}
                <LinkTextButton text={linkText} url={linkUrl}/>
            </Stack>;
        }
    };

    return (
        <Dialog open={show}
                slotProps={{
                    paper: {
                        sx: {
                            width: '800px',
                            maxWidth: '800px',
                            padding: '32px'
                        }
                    }
                }}>
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                flexGrow: 1,
                justifyContent: 'center',
                alignContent: 'center',
            }}>
                <Box sx={{
                    flexDirection: 'column',
                }}>
                    {getTitle()}
                    {getContent()}
                </Box>
            </Box>
        </Dialog>
    );
};

RestrictedAccessDialog.propTypes = {
    show: PropTypes.bool.isRequired,
    config: PropTypes.object.isRequired
};

export {RestrictedAccessDialog};